/**
 * Patient order card — read-only summary of all orders for a patient.
 *
 * Renders a table with status, payment, prescriber, batch, and price columns.
 * Inactive items (deferred/returned/cancelled) are greyed out. Orders linked
 * to an appointment display a small appointment badge on the row.
 */

import { Link } from 'react-router-dom'

import type { OrderUrgency, OrderWithRelations } from '@snootsvet/shared'
import {
  APPOINTMENT_TYPES_BY_ID,
  formatClinicPrice,
  formatName,
  matchesPredicate,
  ORDER_PREDICATES,
} from '@snootsvet/shared'

import { FormattedDate, FormattedDateTime } from '@/frontend/components/formatted-datetime'
import { Badge, type BadgeVariant } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import { ChevronsIcon } from '@/frontend/components/ui/chevrons-icon'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/frontend/components/ui/table'
import { OrderStatusBadge, PaymentBadge } from '@/frontend/features/orders/order-badges'
import { OrderItemCell } from '@/frontend/features/orders/order-item-cell'
import type { PatientGroup } from '@/frontend/features/orders/orders.utils'
import { urgencyRank } from '@/frontend/features/orders/orders.utils'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Highest urgency across a group's active items. */
function groupUrgency(items: OrderWithRelations[]): OrderUrgency | null {
  let highest: OrderUrgency | null = null
  for (const d of items) {
    if (d.urgency && (!highest || urgencyRank[d.urgency] > urgencyRank[highest])) {
      highest = d.urgency
    }
  }
  return highest
}

const urgencyBadgeVariant: Record<OrderUrgency, BadgeVariant> = {
  high: 'destructive',
  medium: 'pending',
  low: 'info',
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface PatientOrderCardProps {
  group: PatientGroup
  /** Called when the user clicks the action button. */
  onSelect: (patientId: string) => void
}

/** Read-only patient order card with urgency badges and per-row appointment indicators. */
export function PatientOrderCard({ group, onSelect }: PatientOrderCardProps) {
  const urgency = groupUrgency(group.items)

  return (
    <div className="rounded-xl bg-card p-4 text-card-foreground ring-1 ring-foreground/10 shadow-xs">
      {/* Patient header */}
      <div>
        <div className="flex items-center gap-3">
          <Link to={`/patients/${group.patientId}`} className="text-lg font-bold hover:underline">
            {group.patientName}
          </Link>
          <Link
            to={`/members/${group.ownerId}`}
            className="text-sm text-muted-foreground hover:underline"
          >
            {group.ownerName}
          </Link>
          {group.appointmentTime && (
            <span className="text-sm text-muted-foreground">
              <FormattedDateTime
                value={group.appointmentTime}
                clinicId={group.appointmentClinicId}
              />
              {group.appointmentTypeId && (
                <>
                  {' · '}
                  {APPOINTMENT_TYPES_BY_ID[group.appointmentTypeId]?.name ??
                    group.appointmentTypeId}
                </>
              )}
            </span>
          )}
          {urgency && urgency !== 'low' && (
            <Badge size="sm" variant={urgencyBadgeVariant[urgency]}>
              <ChevronsIcon level={urgencyRank[urgency] as 1 | 2 | 3} />
              {urgency}
            </Badge>
          )}
          {group.items.some(
            (o) =>
              !matchesPredicate(o, ORDER_PREDICATES.fulfilled) &&
              !['cancelled', 'returned'].includes(o.status)
          ) && (
            <div className="ml-auto">
              <Button onClick={() => onSelect(group.patientId)}>View</Button>
            </div>
          )}
        </div>
      </div>

      {/* Order table */}
      <Table size="sm" variant="borderless" className="mt-2 table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[22%]">Item</TableHead>
            <TableHead className="w-12 text-center">Qty</TableHead>
            <TableHead className="w-[16%]">Instructions</TableHead>
            <TableHead className="w-[10%]">Prescriber</TableHead>
            <TableHead className="w-26 text-center">Status</TableHead>
            <TableHead className="w-20 text-center">Payment</TableHead>
            <TableHead className="w-22 text-center">Created</TableHead>
            <TableHead className="w-22 text-center">Updated</TableHead>
            <TableHead className="w-20 text-right">Price</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {group.items.map((order) => {
            const dimmed =
              order.status === 'cancelled' ||
              order.status === 'returned' ||
              order.status === 'out_of_stock'
            return (
              <TableRow key={order.id} className={dimmed ? 'opacity-50' : undefined}>
                <OrderItemCell order={order} />
                <TableCell className="text-center">{order.quantity}</TableCell>
                <TableCell
                  className="truncate text-muted-foreground"
                  title={order.treatment?.instructions ?? undefined}
                >
                  {order.treatment?.instructions ?? '—'}
                </TableCell>
                <TableCell
                  className="truncate text-muted-foreground"
                  title={order.treatment?.staff ? formatName(order.treatment.staff) : undefined}
                >
                  {order.treatment?.staff ? formatName(order.treatment.staff) : '—'}
                </TableCell>
                <TableCell className="text-center">
                  <OrderStatusBadge status={order.status} size="sm" />
                </TableCell>
                <TableCell className="text-center">
                  <PaymentBadge order={order} size="sm" />
                </TableCell>
                {/* An order was placed at a clinic, so its zone fixes the day. */}
                <TableCell className="text-center">
                  <FormattedDate value={order.createdAt} clinicId={order.clinicId} />
                </TableCell>
                <TableCell className="text-center">
                  <FormattedDate value={order.updatedAt} clinicId={order.clinicId} />
                </TableCell>
                <TableCell className="text-right">
                  {formatClinicPrice(order.totalPrice, order.clinicId)}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
